import React, { useState, useEffect, useCallback } from 'react';
import { toViewerZone, viewerTz, formatTime12, aptTimeLabel } from '../utils/timeZone';
import { ParticipantSeesLine } from '../components/ParticipantSeesLine';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Clock,
  User,
  MapPin,
  Video,
  Phone,
  Loader2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  Filter,
  ExternalLink,
  Check,
  X,
  Edit,
  Trash2,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, addWeeks, subWeeks, isSameMonth, isSameDay, isSameWeek, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useAuth } from '../contexts/AuthContext';
import { AppointmentCaseManagerSelect } from '../components/CaseAppointmentFields';
import { Link, useNavigate } from 'react-router-dom';
import { Switch } from '../components/ui/switch';
import { MessageSquare, Repeat } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback appointment types (used until API loads)
const DEFAULT_APPOINTMENT_TYPES = [
  { id: 'initial_assessment', label: 'Initial Assessment', duration: 60, color: '#3b82f6' },
  { id: 'follow_up', label: 'Follow-up Meeting', duration: 30, color: '#22c55e' },
  { id: 'case_review', label: 'Case Review', duration: 45, color: '#a855f7' },
  { id: 'intake', label: 'Intake Appointment', duration: 90, color: '#f97316' },
  { id: 'phone_call', label: 'Phone Call', duration: 15, color: '#14b8a6' },
  { id: 'training', label: 'Training Session', duration: 120, color: '#6366f1' },
  { id: 'other', label: 'Other', duration: 30, color: '#6b7280' }
];

// Time slots for day/week view (6 AM to 9 PM)
const TIME_SLOTS = Array.from({ length: 16 }, (_, i) => {
  const hour = i + 6;
  return {
    hour,
    label: hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`,
    time: `${String(hour).padStart(2, '0')}:00`
  };
});

const AppointmentsPage = () => {
  const { user } = useAuth();
  const [staffList, setStaffList] = useState([]);
  useEffect(() => {
    axios.get(`${API}/users/staff`).then((res) => setStaffList(res.data.users || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (user?.id) setNewAppointment(prev => (prev.case_manager_id ? prev : { ...prev, case_manager_id: user.id }));
  }, [user?.id]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState(DEFAULT_APPOINTMENT_TYPES);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [viewMode, setViewMode] = useState('month');
  const [searchTerm, setSearchTerm] = useState('');
  const [userTimezone, setUserTimezone] = useState('America/Chicago');
  const [participants, setParticipants] = useState([]);
  const [m365Connected, setM365Connected] = useState(false);
  const [m365Status, setM365Status] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [snapOutreachParticipants, setSnapOutreachParticipants] = useState([]);
  const [loadingSnapParticipants, setLoadingSnapParticipants] = useState(false);
  const [activeTab, setActiveTab] = useState('calendar');
  const [snapAppointments, setSnapAppointments] = useState([]);
  const [loadingSnapAppointments, setLoadingSnapAppointments] = useState(false);
  const [deletingAppointmentId, setDeletingAppointmentId] = useState(null);
  
  // Check if user has permission to edit/delete any appointment
  // This includes roles (system_admin, executive, operations_manager) OR the user-specific permission
  const canManageAllAppointments = ['system_admin', 'executive', 'operations_manager'].includes(user?.role) || user?.can_manage_all_appointments;
  
  // Check if a specific appointment belongs to the current user
  const isOwnAppointment = (appointment) => {
    return appointment?.created_by === user?.id || appointment?.user_id === user?.id;
  };
  
  // Check if user can edit/delete a specific appointment
  const canManageAppointment = (appointment) => {
    return canManageAllAppointments || isOwnAppointment(appointment);
  };
  
  // SNAP Appointments filters and sorting
  const [snapFilters, setSnapFilters] = useState({
    startDate: '',
    endDate: '',
    status: '_all'
  });
  const [snapSortField, setSnapSortField] = useState('date');
  const [snapSortDirection, setSnapSortDirection] = useState('desc');

  const [newAppointment, setNewAppointment] = useState({
    title: '',
    appointment_type: '',
    participant_id: '',
    participant_name: '',
    case_manager_id: null,
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '09:30',
    location: '',
    location_type: 'in_person', // in_person, phone, video
    notes: '',
    reminder_minutes: 30,
    sync_to_m365: true,
    // Recurring appointment fields
    is_recurring: false,
    recurrence_interval: 7,
    recurrence_unit: 'days',
    recurrence_end_date: ''
  });

  // Fetch appointment types from API
  const fetchAppointmentTypes = async () => {
    try {
      const response = await axios.get(`${API}/appointment-types`);
      const types = response.data.appointment_types || [];
      if (types.length > 0) {
        setAppointmentTypes(types);
        // Set default appointment type to first one if not already set
        if (!newAppointment.appointment_type && types.length > 0) {
          setNewAppointment(prev => ({ ...prev, appointment_type: types[0].id }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
      // Keep using default types on error
    }
  };

  useEffect(() => {
    fetchAppointmentTypes();
    fetchAppointments();
    fetchParticipants();
    fetchSystemSettings();
    checkM365Connection();
  }, [currentDate]);

  // Fetch SNAP appointments when the SNAP tab is selected
  useEffect(() => {
    if (activeTab === 'snap') {
      fetchSnapAppointments();
    }
  }, [activeTab]);

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/system/settings`);
      if (response.data?.timezone) {
        setUserTimezone(response.data.timezone);
      }
    } catch (error) {
      // Use default timezone
    }
  };

  const checkM365Connection = async () => {
    try {
      const response = await axios.get(`${API}/integrations/m365/status`);
      setM365Connected(response.data?.connected || false);
      setM365Status(response.data);
    } catch (error) {
      setM365Connected(false);
      setM365Status(null);
    }
  };

  const handleSyncCalendar = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/integrations/m365/calendar/sync`);
      const results = response.data;
      
      if (results.pushed_to_outlook > 0) {
        toast.success(`Synced ${results.pushed_to_outlook} appointment(s) to Outlook`);
      } else if (results.errors?.length > 0) {
        toast.warning('Sync completed with some errors');
      } else {
        toast.success('Calendar sync complete');
      }
      
      // Refresh appointments
      fetchAppointments();
      checkM365Connection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setShowEditDialog(true);
  };

  const handleUpdateAppointment = async () => {
    if (!editingAppointment) return;
    
    // Validate status is set
    if (!editingAppointment.status) {
      toast.error('Please select a status');
      return;
    }
    
    // Validate reschedule data if status is Rescheduled
    if (editingAppointment.status === 'Rescheduled') {
      if (!editingAppointment.rescheduled_to_date || !editingAppointment.rescheduled_to_time) {
        toast.error('Please enter the rescheduled date and time');
        return;
      }
    }
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/appointments/${editingAppointment.id}`, editingAppointment);
      
      if (editingAppointment.status === 'Rescheduled' && response.data.new_appointment_id) {
        toast.success(`Appointment rescheduled. New appointment created for ${editingAppointment.rescheduled_to_date}`);
      } else {
        toast.success('Appointment updated');
      }
      
      setShowEditDialog(false);
      setEditingAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment? This action cannot be undone.')) return;
    
    setDeletingAppointmentId(appointmentId);
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      toast.success('Appointment deleted');
      fetchAppointments();
      // Also refresh SNAP appointments if on that tab
      if (activeTab === 'snap') {
        fetchSnapAppointments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete appointment');
    } finally {
      setDeletingAppointmentId(null);
    }
  };

  const fetchAppointments = async () => {
    try {
      const start = format(startOfMonth(subMonths(currentDate, 1)), 'yyyy-MM-dd');
      const end = format(endOfMonth(addMonths(currentDate, 1)), 'yyyy-MM-dd');
      
      // Filter to logged-in user's appointments only (personal calendar view)
      const response = await axios.get(`${API}/appointments`, {
        params: { 
          start_date: start, 
          end_date: end,
          user_id: user?.id  // Only show appointments created by or assigned to this user
        }
      });
      setAppointments((response.data.appointments || []).map(a => toViewerZone(a, viewerTz(user))));
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      // Use mock data for now
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      const response = await axios.get(`${API}/participants?limit=100`);
      // API returns 'items' not 'participants'
      setParticipants(response.data.items || response.data.participants || []);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
    }
  };

  // Fetch SNAP Outreach participants for filtered dropdown
  const fetchSnapOutreachParticipants = async () => {
    setLoadingSnapParticipants(true);
    try {
      const response = await axios.get(`${API}/participants/snap-outreach`);
      setSnapOutreachParticipants(response.data.participants || []);
    } catch (error) {
      console.error('Failed to fetch SNAP Outreach participants:', error);
      setSnapOutreachParticipants([]);
    } finally {
      setLoadingSnapParticipants(false);
    }
  };

  // Check if current appointment type is SNAP Application Assistance
  const isSnapApplicationAssistance = (typeId) => {
    if (!typeId) return false;
    const normalizedType = typeId.toLowerCase();
    return normalizedType.includes('snap_application_assistance') || 
           normalizedType.includes('snap application assistance');
  };

  // Fetch all SNAP Application Assistance appointments (visible to all users)
  const fetchSnapAppointments = async () => {
    setLoadingSnapAppointments(true);
    try {
      const params = new URLSearchParams();
      if (snapFilters.startDate) params.append('start_date', snapFilters.startDate);
      if (snapFilters.endDate) params.append('end_date', snapFilters.endDate);
      if (snapFilters.status && snapFilters.status !== '_all') params.append('status', snapFilters.status);
      
      const response = await axios.get(`${API}/appointments/snap-application-assistance?${params.toString()}`);
      setSnapAppointments(response.data.appointments || []);
    } catch (error) {
      console.error('Failed to fetch SNAP Application Assistance appointments:', error);
      setSnapAppointments([]);
    } finally {
      setLoadingSnapAppointments(false);
    }
  };

  // Filter and sort SNAP appointments
  const getFilteredAndSortedSnapAppointments = () => {
    let filtered = [...snapAppointments];
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (snapSortField) {
        case 'date':
          aVal = `${a.date || ''} ${a.start_time || ''}`;
          bVal = `${b.date || ''} ${b.start_time || ''}`;
          break;
        case 'status':
          aVal = (a.status || 'Scheduled').toLowerCase();
          bVal = (b.status || 'Scheduled').toLowerCase();
          break;
        case 'participant':
          aVal = (a.participant_name || '').toLowerCase();
          bVal = (b.participant_name || '').toLowerCase();
          break;
        default:
          aVal = a.date || '';
          bVal = b.date || '';
      }
      
      if (snapSortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    return filtered;
  };

  // Toggle sort for SNAP appointments
  const handleSnapSort = (field) => {
    if (snapSortField === field) {
      setSnapSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSnapSortField(field);
      setSnapSortDirection('desc');
    }
  };

  // Export SNAP appointments to CSV
  const exportSnapAppointmentsToCSV = () => {
    const data = getFilteredAndSortedSnapAppointments();
    
    if (data.length === 0) {
      toast.error('No appointments to export');
      return;
    }
    
    // CSV headers
    const headers = [
      'Date',
      'Time',
      'Title',
      'Status',
      'Participant',
      'Location Type',
      'Location',
      'Scheduled By',
      'Notes'
    ];
    
    // CSV rows
    const rows = data.map(apt => [
      apt.date || '',
      `${apt.start_time || ''}${apt.end_time ? ' - ' + apt.end_time : ''}`,
      apt.title || 'SNAP Application Assistance',
      apt.status || 'Scheduled',
      apt.participant_name || '',
      apt.location_type === 'in_person' ? 'In Person' : apt.location_type === 'phone' ? 'Phone' : apt.location_type === 'video' ? 'Video' : '',
      apt.location || '',
      apt.created_by_name || '',
      (apt.notes || '').replace(/"/g, '""').replace(/\n/g, ' ')
    ]);
    
    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `snap_application_assistance_appointments_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${data.length} appointments to CSV`);
  };

  // Get the filtered participants list based on appointment type
  const getFilteredParticipants = () => {
    if (isSnapApplicationAssistance(newAppointment.appointment_type)) {
      return snapOutreachParticipants;
    }
    return participants;
  };

  const handleCreateAppointment = async () => {
    if (!newAppointment.title || !newAppointment.date || !newAppointment.start_time) {
      toast.error('Please fill in required fields');
      return;
    }

    // Validate recurring appointments
    if (newAppointment.is_recurring && !newAppointment.recurrence_end_date) {
      toast.error('Please set an end date for the recurring appointment');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/appointments`, {
        ...newAppointment,
        recurrence_start_date: newAppointment.date,
        created_by: user?.id,
        created_by_name: user?.name
      });
      
      if (response.data?.series_id) {
        toast.success(`Created ${response.data.occurrences_created} recurring appointments`);
      } else {
        toast.success('Appointment created successfully');
        if (response.data?.m365_event_id) {
          toast.success('Synced to Microsoft 365 Calendar');
        }
      }
      
      setShowNewDialog(false);
      resetNewAppointment();
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setSaving(false);
    }
  };

  const resetNewAppointment = () => {
    // Get the first appointment type or default
    const defaultType = appointmentTypes.length > 0 ? appointmentTypes[0].id : '';
    setNewAppointment({
      title: '',
      appointment_type: defaultType,
      participant_id: '',
      participant_name: '',
      case_manager_id: user?.id || null,
      date: format(selectedDate, 'yyyy-MM-dd'),
      start_time: '09:00',
      end_time: '09:30',
      location: '',
      location_type: 'in_person',
      notes: '',
      reminder_minutes: 30,
      sync_to_m365: true,
      is_recurring: false,
      recurrence_interval: 7,
      recurrence_unit: 'days',
      recurrence_end_date: ''
    });
  };

  const handleTypeChange = (typeId) => {
    const type = appointmentTypes.find(t => t.id === typeId);
    if (type) {
      setNewAppointment(prev => {
        // Auto-calculate end time
        const [hours, minutes] = prev.start_time.split(':').map(Number);
        const startMinutes = hours * 60 + minutes;
        const endMinutes = startMinutes + type.duration;
        const endHours = Math.floor(endMinutes / 60);
        const endMins = endMinutes % 60;
        
        return {
          ...prev,
          appointment_type: typeId,
          title: prev.title || type.label,
          end_time: `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`,
          // Clear participant selection when switching types
          participant_id: '',
          participant_name: '',
          case_manager_id: user?.id || null
        };
      });
      
      // Fetch SNAP Outreach participants if this is SNAP Application Assistance type
      if (isSnapApplicationAssistance(typeId)) {
        fetchSnapOutreachParticipants();
      }
    }
  };

  const handleParticipantSelect = (participantId) => {
    // Search in both participant lists
    const allParticipants = [...participants, ...snapOutreachParticipants];
    const participant = allParticipants.find(p => p.id === participantId);
    setNewAppointment(prev => ({
      ...prev,
      participant_id: participantId,
      participant_name: participant?.full_name || '',
      case_manager_id: user?.id || null
    }));
    // Picker starts on the participant's case manager (open case first)
    if (participantId && participantId !== '_none') {
      axios.get(`${API}/participants/${participantId}/cases`).then((res) => {
        const cases = res.data.items || [];
        const c = cases.find((x) => x.assigned_staff_id && !/closed/i.test(x.status || '')) || cases.find((x) => x.assigned_staff_id);
        if (c) setNewAppointment(prev => (prev.participant_id === participantId ? { ...prev, case_manager_id: c.assigned_staff_id } : prev));
      }).catch(() => {});
    }
  };

  // Calendar rendering
  const renderCalendar = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const dateFormat = 'd';
    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const dayAppointments = appointments.filter(apt => 
          apt.date === format(day, 'yyyy-MM-dd')
        );

        days.push(
          <div
            key={day.toString()}
            className={`min-h-[100px] p-1 border-r border-b cursor-pointer transition-colors hover:bg-muted/50
              ${!isSameMonth(day, monthStart) ? 'bg-muted/30 text-muted-foreground' : ''}
              ${isSameDay(day, selectedDate) ? 'bg-primary/10 ring-2 ring-primary' : ''}
              ${isSameDay(day, new Date()) ? 'bg-blue-50 dark:bg-blue-950/20' : ''}
            `}
            onClick={() => {
              setSelectedDate(cloneDay);
              setNewAppointment(prev => ({ ...prev, date: format(cloneDay, 'yyyy-MM-dd') }));
            }}
          >
            <div className="flex justify-between items-start">
              <span className={`text-sm font-medium ${isSameDay(day, new Date()) ? 'text-primary' : ''}`}>
                {format(day, dateFormat)}
              </span>
              {dayAppointments.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {dayAppointments.length}
                </Badge>
              )}
            </div>
            <div className="space-y-1 mt-1">
              {dayAppointments.slice(0, 3).map((apt, idx) => {
                const typeInfo = appointmentTypes.find(t => t.id === apt.appointment_type);
                return (
                  <div 
                    key={idx} 
                    className={`text-xs p-1 rounded truncate ${typeInfo?.color || 'bg-gray-500'} text-white`}
                  >
                    {formatTime12(apt.start_time)} {apt.title}
                  </div>
                );
              })}
              {dayAppointments.length > 3 && (
                <p className="text-xs text-muted-foreground">+{dayAppointments.length - 3} more</p>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }

    return rows;
  };

  // Day view rendering
  const renderDayView = () => {
    const dayStr = format(selectedDate, 'yyyy-MM-dd');
    const dayAppointments = appointments.filter(apt => apt.date === dayStr);
    
    // Helper to get appointment position
    const getAppointmentPosition = (startTime) => {
      const [hours, minutes] = startTime.split(':').map(Number);
      const startHour = 6; // 6 AM start
      const topOffset = ((hours - startHour) * 60 + minutes) * (60 / 60); // 60px per hour
      return topOffset;
    };

    const getAppointmentHeight = (startTime, endTime) => {
      const [startH, startM] = startTime.split(':').map(Number);
      const [endH, endM] = endTime.split(':').map(Number);
      const durationMinutes = (endH * 60 + endM) - (startH * 60 + startM);
      return Math.max(durationMinutes, 30); // Min 30px height
    };

    return (
      <div className="relative">
        {/* Time grid */}
        <div className="grid grid-cols-[80px_1fr]">
          {TIME_SLOTS.map((slot) => (
            <React.Fragment key={slot.hour}>
              <div className="h-[60px] border-b border-r pr-2 text-right text-xs text-muted-foreground pt-1">
                {slot.label}
              </div>
              <div 
                className="h-[60px] border-b relative hover:bg-muted/30 cursor-pointer"
                onClick={() => {
                  setNewAppointment(prev => ({
                    ...prev,
                    date: dayStr,
                    start_time: slot.time,
                    end_time: `${String(slot.hour + 1).padStart(2, '0')}:00`
                  }));
                  setShowNewDialog(true);
                }}
              />
            </React.Fragment>
          ))}
        </div>
        
        {/* Appointments overlay */}
        <div className="absolute top-0 left-[80px] right-0">
          {dayAppointments.map((apt) => {
            const typeInfo = appointmentTypes.find(t => t.id === apt.appointment_type);
            const top = getAppointmentPosition(apt.start_time);
            const height = getAppointmentHeight(apt.start_time, apt.end_time);
            
            return (
              <div
                key={apt.id}
                className={`absolute left-1 right-1 rounded-md p-2 ${canManageAppointment(apt) ? 'cursor-pointer' : 'cursor-default'} ${typeInfo?.color || 'bg-gray-500'} text-white text-xs overflow-hidden`}
                style={{ top: `${top}px`, height: `${height}px`, minHeight: '30px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (canManageAppointment(apt)) {
                    handleEditAppointment(apt);
                  }
                }}
              >
                <div className="font-medium truncate">{apt.title}</div>
                <div className="opacity-80">{aptTimeLabel(apt)}</div>
                {apt.participant_name && height > 50 && (
                  <div className="opacity-80 truncate">{apt.participant_name}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Week view rendering
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    
    return (
      <div className="overflow-x-auto">
        {/* Week header */}
        <div className="grid grid-cols-[80px_repeat(7,1fr)] border-b sticky top-0 bg-background z-10">
          <div className="p-2 border-r" />
          {weekDays.map((day) => (
            <div 
              key={day.toString()} 
              className={`p-2 text-center border-r cursor-pointer hover:bg-muted/50 ${
                isSameDay(day, new Date()) ? 'bg-primary/10' : ''
              } ${isSameDay(day, selectedDate) ? 'ring-2 ring-primary ring-inset' : ''}`}
              onClick={() => setSelectedDate(day)}
            >
              <div className="text-xs text-muted-foreground">{format(day, 'EEE')}</div>
              <div className={`text-lg font-semibold ${isSameDay(day, new Date()) ? 'text-primary' : ''}`}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>
        
        {/* Time grid */}
        <div className="relative">
          {TIME_SLOTS.map((slot) => (
            <div key={slot.hour} className="grid grid-cols-[80px_repeat(7,1fr)]">
              <div className="h-[60px] border-b border-r pr-2 text-right text-xs text-muted-foreground pt-1">
                {slot.label}
              </div>
              {weekDays.map((day) => {
                const dayStr = format(day, 'yyyy-MM-dd');
                const slotAppointments = appointments.filter(apt => 
                  apt.date === dayStr && 
                  apt.start_time >= slot.time && 
                  apt.start_time < `${String(slot.hour + 1).padStart(2, '0')}:00`
                );
                
                return (
                  <div 
                    key={`${day}-${slot.hour}`}
                    className="h-[60px] border-b border-r relative hover:bg-muted/30 cursor-pointer p-0.5"
                    onClick={() => {
                      setSelectedDate(day);
                      setNewAppointment(prev => ({
                        ...prev,
                        date: dayStr,
                        start_time: slot.time,
                        end_time: `${String(slot.hour + 1).padStart(2, '0')}:00`
                      }));
                      setShowNewDialog(true);
                    }}
                  >
                    {slotAppointments.map((apt) => {
                      const typeInfo = appointmentTypes.find(t => t.id === apt.appointment_type);
                      return (
                        <div
                          key={apt.id}
                          className={`rounded p-1 ${canManageAppointment(apt) ? 'cursor-pointer' : 'cursor-default'} ${typeInfo?.color || 'bg-gray-500'} text-white text-xs truncate mb-0.5`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (canManageAppointment(apt)) {
                              handleEditAppointment(apt);
                            }
                          }}
                        >
                          {formatTime12(apt.start_time)} {apt.title}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Navigation handlers based on view mode
  const handlePrevious = () => {
    if (viewMode === 'day') {
      setSelectedDate(addDays(selectedDate, -1));
      setCurrentDate(addDays(selectedDate, -1));
    } else if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'day') {
      setSelectedDate(addDays(selectedDate, 1));
      setCurrentDate(addDays(selectedDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const getViewTitle = () => {
    if (viewMode === 'day') {
      return format(selectedDate, 'EEEE, MMMM d, yyyy');
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      if (isSameMonth(weekStart, weekEnd)) {
        return `${format(weekStart, 'MMMM d')} - ${format(weekEnd, 'd, yyyy')}`;
      }
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'MMMM yyyy');
  };

  // Selected day appointments
  const selectedDayAppointments = appointments.filter(apt => 
    apt.date === format(selectedDate, 'yyyy-MM-dd')
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CalendarIcon className="w-8 h-8 text-primary" />
            Appointments
          </h1>
          <p className="text-muted-foreground mt-1">
            Schedule and manage appointments ({userTimezone})
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {m365Connected && (
            <Badge variant="outline" className="text-green-600">
              <Check className="w-3 h-3 mr-1" />
              M365 Connected
            </Badge>
          )}
          {m365Status?.last_sync && (
            <span className="text-xs text-muted-foreground hidden sm:inline">
              Last sync: {new Date(m365Status.last_sync).toLocaleTimeString()}
            </span>
          )}
          <Button 
            variant="outline" 
            onClick={m365Connected ? handleSyncCalendar : fetchAppointments}
            disabled={syncing}
            data-testid="sync-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {m365Connected ? (syncing ? 'Syncing...' : 'Sync with Outlook') : 'Refresh'}
          </Button>
          <Button onClick={() => setShowNewDialog(true)} data-testid="new-appointment-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Appointment
          </Button>
        </div>
      </div>

      {/* Management Calendar Link - Only for Ops Managers and above */}
      {['system_admin', 'executive', 'operations_manager'].includes(user?.role) && (
        <Card className="border-dashed bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <CalendarIcon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Management Calendar</h3>
                  <p className="text-sm text-muted-foreground">
                    Centralized view of all appointments, deadlines, and staff schedules
                  </p>
                </div>
              </div>
              <Button asChild data-testid="management-calendar-link">
                <Link to="/appointments/management-calendar">
                  Open Management Calendar
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Calendar and SNAP Application Assistance */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="snap" data-testid="tab-snap-appointments">
            SNAP Application Assistance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <Card className="card-shadow lg:col-span-3">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="text-xl">
                {getViewTitle()}
              </CardTitle>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="flex rounded-lg border p-1" data-testid="view-mode-toggle">
                  <Button 
                    variant={viewMode === 'day' ? 'secondary' : 'ghost'} 
                    size="sm"
                    className="px-3 h-7"
                    onClick={() => setViewMode('day')}
                    data-testid="day-view-btn"
                  >
                    Day
                  </Button>
                  <Button 
                    variant={viewMode === 'week' ? 'secondary' : 'ghost'} 
                    size="sm"
                    className="px-3 h-7"
                    onClick={() => setViewMode('week')}
                    data-testid="week-view-btn"
                  >
                    Week
                  </Button>
                  <Button 
                    variant={viewMode === 'month' ? 'secondary' : 'ghost'} 
                    size="sm"
                    className="px-3 h-7"
                    onClick={() => setViewMode('month')}
                    data-testid="month-view-btn"
                  >
                    Month
                  </Button>
                </div>
                
                {/* Navigation */}
                <div className="flex gap-1">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={handlePrevious}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-8" onClick={handleToday}>
                    Today
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleNext}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Month View */}
            {viewMode === 'month' && (
              <>
                {/* Week headers */}
                <div className="grid grid-cols-7 border-t border-l">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="p-2 text-center text-sm font-medium border-r border-b bg-muted">
                      {day}
                    </div>
                  ))}
                </div>
                {/* Calendar grid */}
                <div className="border-l">
                  {renderCalendar()}
                </div>
              </>
            )}
            
            {/* Week View */}
            {viewMode === 'week' && (
              <div className="border rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
                {renderWeekView()}
              </div>
            )}
            
            {/* Day View */}
            {viewMode === 'day' && (
              <div className="border rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
                {renderDayView()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Day Details */}
        <div className="space-y-4">
          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                {format(selectedDate, 'EEEE, MMMM d')}
              </CardTitle>
              <CardDescription>
                {selectedDayAppointments.length} appointment{selectedDayAppointments.length !== 1 ? 's' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedDayAppointments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No appointments</p>
                  <Button 
                    variant="link" 
                    onClick={() => setShowNewDialog(true)}
                    className="mt-2"
                  >
                    Schedule one
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedDayAppointments.map((apt, idx) => {
                    const typeInfo = appointmentTypes.find(t => t.id === apt.appointment_type);
                    return (
                      <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 group">
                        <div className="flex items-start gap-3">
                          <div className={`w-2 min-h-[40px] rounded-full ${typeInfo?.color || 'bg-gray-500'}`} />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <p className="font-medium">{apt.title}</p>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {/* Edit button - shown if user can manage this appointment */}
                                {canManageAppointment(apt) && (
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-6 w-6"
                                    onClick={() => handleEditAppointment(apt)}
                                    title="Edit appointment"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                )}
                                {/* Delete button - shown if user can manage this appointment */}
                                {canManageAppointment(apt) && (
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-6 w-6 text-red-500 hover:text-red-600"
                                    onClick={() => handleDeleteAppointment(apt.id)}
                                    disabled={deletingAppointmentId === apt.id}
                                    title="Delete appointment"
                                  >
                                    {deletingAppointmentId === apt.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Trash2 className="w-3 h-3" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                              <Clock className="w-3 h-3" />
                              {aptTimeLabel(apt)}
                            </div>
                            {apt.participant_name && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                <User className="w-3 h-3" />
                                {apt.participant_name}
                              </div>
                            )}
                            {apt.location && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                <MapPin className="w-3 h-3" />
                                {apt.location}
                              </div>
                            )}
                            {/* Outlook sync indicator - hidden for SNAP Application Assistance */}
                            {apt.m365_event_id && !isSnapApplicationAssistance(apt.appointment_type) && (
                              <Badge variant="outline" className="mt-2 text-xs">
                                <Check className="w-3 h-3 mr-1" />
                                Synced to Outlook
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">This Month</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-medium">
                    {appointments.filter(a => a.date?.startsWith(format(currentDate, 'yyyy-MM'))).length}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Upcoming</span>
                  <span className="font-medium">
                    {appointments.filter(a => a.date >= format(new Date(), 'yyyy-MM-dd')).length}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Appointment Types Legend */}
          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Appointment Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {appointmentTypes.slice(0, 5).map(type => (
                  <div key={type.id} className="flex items-center gap-2 text-sm">
                    <div className={`w-3 h-3 rounded-full ${type.color}`} />
                    <span>{type.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
        </TabsContent>

        {/* SNAP Application Assistance Tab */}
        <TabsContent value="snap">
          <Card className="card-shadow">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">SNAP Application Assistance Appointments</CardTitle>
                    <CardDescription>
                      All SNAP Application Assistance appointments across the organization ({getFilteredAndSortedSnapAppointments().length} total)
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={exportSnapAppointmentsToCSV}
                      disabled={loadingSnapAppointments || snapAppointments.length === 0}
                      data-testid="export-snap-csv"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={fetchSnapAppointments}
                      disabled={loadingSnapAppointments}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingSnapAppointments ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </div>
                
                {/* Filters */}
                <div className="flex flex-wrap items-end gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-xs">Start Date</Label>
                    <Input
                      type="date"
                      value={snapFilters.startDate}
                      onChange={(e) => setSnapFilters(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-40 h-9"
                      data-testid="snap-filter-start-date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">End Date</Label>
                    <Input
                      type="date"
                      value={snapFilters.endDate}
                      onChange={(e) => setSnapFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-40 h-9"
                      data-testid="snap-filter-end-date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Status</Label>
                    <Select
                      value={snapFilters.status}
                      onValueChange={(v) => setSnapFilters(prev => ({ ...prev, status: v }))}
                    >
                      <SelectTrigger className="w-40 h-9">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All Statuses</SelectItem>
                        <SelectItem value="Scheduled">Scheduled</SelectItem>
                        <SelectItem value="Attended">Attended</SelectItem>
                        <SelectItem value="No Show">No Show</SelectItem>
                        <SelectItem value="Rescheduled">Rescheduled</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={fetchSnapAppointments}
                    disabled={loadingSnapAppointments}
                    className="h-9"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Apply Filters
                  </Button>
                  {(snapFilters.startDate || snapFilters.endDate || snapFilters.status !== '_all') && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setSnapFilters({ startDate: '', endDate: '', status: '_all' });
                        setTimeout(fetchSnapAppointments, 100);
                      }}
                      className="h-9"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingSnapAppointments ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : getFilteredAndSortedSnapAppointments().length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No SNAP Application Assistance appointments</p>
                  <p className="text-sm">
                    {snapFilters.startDate || snapFilters.endDate || snapFilters.status !== '_all' 
                      ? 'Try adjusting your filters.' 
                      : 'Appointments of this type will appear here when scheduled.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Sortable Headers */}
                  <div className="flex items-center gap-4 px-4 py-2 bg-muted/30 rounded-lg text-sm font-medium text-muted-foreground">
                    <div className="w-2" /> {/* Color indicator spacer */}
                    <button 
                      className="flex items-center gap-1 hover:text-foreground transition-colors min-w-[120px]"
                      onClick={() => handleSnapSort('date')}
                      data-testid="sort-by-date"
                    >
                      Date
                      {snapSortField === 'date' ? (
                        snapSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-50" />
                      )}
                    </button>
                    <div className="flex-1 min-w-[150px]">Title</div>
                    <button 
                      className="flex items-center gap-1 hover:text-foreground transition-colors min-w-[100px]"
                      onClick={() => handleSnapSort('status')}
                      data-testid="sort-by-status"
                    >
                      Status
                      {snapSortField === 'status' ? (
                        snapSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-50" />
                      )}
                    </button>
                    <button 
                      className="flex items-center gap-1 hover:text-foreground transition-colors min-w-[140px]"
                      onClick={() => handleSnapSort('participant')}
                      data-testid="sort-by-participant"
                    >
                      Participant
                      {snapSortField === 'participant' ? (
                        snapSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-50" />
                      )}
                    </button>
                    <div className="w-10" /> {/* Actions spacer */}
                  </div>
                  
                  {/* Appointment List */}
                  <div className="space-y-2">
                    {getFilteredAndSortedSnapAppointments().map((apt) => {
                    const typeInfo = appointmentTypes.find(t => t.id === apt.appointment_type);
                    const isPast = new Date(`${apt.date}T${apt.end_time || apt.start_time || '23:59'}`) < new Date();
                    
                    return (
                      <div 
                        key={apt.id} 
                        className={`p-4 border rounded-lg hover:bg-muted/50 transition-colors ${isPast ? 'opacity-60' : ''}`}
                        data-testid={`snap-appointment-${apt.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            {/* Color indicator */}
                            <div 
                              className="w-2 min-h-[60px] rounded-full flex-shrink-0"
                              style={{ backgroundColor: typeInfo?.color || '#ec4899' }}
                            />
                            
                            <div className="flex-1">
                              {/* Title and badges */}
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h4 className="font-medium">{apt.title || 'SNAP Application Assistance'}</h4>
                                <Badge className={
                                  apt.status?.toLowerCase() === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                  apt.status?.toLowerCase() === 'attended' || apt.status?.toLowerCase() === 'completed' ? 'bg-green-100 text-green-800' :
                                  apt.status?.toLowerCase() === 'no show' ? 'bg-red-100 text-red-800' :
                                  apt.status?.toLowerCase() === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                  apt.status?.toLowerCase() === 'rescheduled' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-blue-100 text-blue-800'
                                }>
                                  {apt.status || 'Scheduled'}
                                </Badge>
                                {isPast && <Badge variant="outline" className="text-xs">Past</Badge>}
                              </div>
                              
                              {/* Date and time */}
                              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                                <span className="flex items-center gap-1">
                                  <CalendarIcon className="w-3 h-3" />
                                  {apt.date ? format(parseISO(apt.date), 'MMM d, yyyy') : '-'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {aptTimeLabel(apt)}
                                  {apt.participant_tz_missing && <span className="ml-1 text-[10px] text-amber-600" data-testid={`apt-tz-missing-${apt.id}`}>(participant's time zone isn't set)</span>}
                                </span>
                                {apt.location_type && (
                                  <span className="flex items-center gap-1">
                                    {apt.location_type === 'phone' ? <Phone className="w-3 h-3" /> :
                                     apt.location_type === 'video' ? <Video className="w-3 h-3" /> :
                                     <MapPin className="w-3 h-3" />}
                                    {apt.location_type === 'in_person' ? 'In Person' : 
                                     apt.location_type === 'phone' ? 'Phone' : 'Video'}
                                  </span>
                                )}
                              </div>
                              
                              {/* Participant and staff */}
                              <div className="flex items-center gap-4 text-sm">
                                {apt.participant_name && (
                                  <span className="flex items-center gap-1 text-muted-foreground">
                                    <User className="w-3 h-3" />
                                    {apt.participant_name}
                                  </span>
                                )}
                                {apt.created_by_name && (
                                  <span className="text-muted-foreground">
                                    Scheduled by: {apt.created_by_name}
                                  </span>
                                )}
                              </div>
                              
                              {/* Location */}
                              {apt.location && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  <MapPin className="w-3 h-3 inline mr-1" />
                                  {apt.location}
                                </p>
                              )}
                              
                              {/* Notes */}
                              {apt.notes && (
                                <p className="text-sm text-muted-foreground mt-2 italic line-clamp-2">
                                  {apt.notes}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {/* Quick Link to Case - only show if participant is linked */}
                            {apt.participant_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Navigate to participant's cases - find the first open case
                                  navigate(`/participants/${apt.participant_id}?tab=cases`);
                                }}
                                title="View participant's cases"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Edit button - visible if user can manage this appointment */}
                            {canManageAppointment(apt) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingAppointment(apt);
                                  setShowEditDialog(true);
                                  // Fetch SNAP Outreach participants for the edit dialog
                                  if (isSnapApplicationAssistance(apt.appointment_type)) {
                                    fetchSnapOutreachParticipants();
                                  }
                                }}
                                title="Edit appointment"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Delete button - visible if user can manage this appointment */}
                            {canManageAppointment(apt) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteAppointment(apt.id)}
                                disabled={deletingAppointmentId === apt.id}
                                title="Delete appointment"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                {deletingAppointmentId === apt.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Appointment Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule Appointment</DialogTitle>
            <DialogDescription>
              Create a new appointment for {format(selectedDate, 'MMMM d, yyyy')}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Appointment title"
                value={newAppointment.title}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, title: e.target.value }))}
                data-testid="appointment-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={newAppointment.appointment_type}
                  onValueChange={handleTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${type.color}`} />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location Type</Label>
                <Select
                  value={newAppointment.location_type}
                  onValueChange={(v) => setNewAppointment(prev => ({ ...prev, location_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> In Person
                      </div>
                    </SelectItem>
                    <SelectItem value="phone">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Phone Call
                      </div>
                    </SelectItem>
                    <SelectItem value="video">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4" /> Video Call
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newAppointment.date}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={newAppointment.start_time}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, start_time: e.target.value }))}
                />
                <ParticipantSeesLine participantId={newAppointment.participant_id} date={newAppointment.date} time={newAppointment.start_time} />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={newAppointment.end_time}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Participant (Optional)
                {isSnapApplicationAssistance(newAppointment.appointment_type) && (
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    — Showing SNAP Outreach participants only
                  </span>
                )}
              </Label>
              <Select
                value={newAppointment.participant_id}
                onValueChange={handleParticipantSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    loadingSnapParticipants 
                      ? "Loading participants..." 
                      : "Select a participant"
                  } />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No participant</SelectItem>
                  {getFilteredParticipants().map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                  ))}
                  {isSnapApplicationAssistance(newAppointment.appointment_type) && 
                   getFilteredParticipants().length === 0 && 
                   !loadingSnapParticipants && (
                    <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                      No participants with SNAP Outreach enrollment found
                    </div>
                  )}
                </SelectContent>
              </Select>
              {isSnapApplicationAssistance(newAppointment.appointment_type) && (
                <p className="text-xs text-muted-foreground">
                  For SNAP Application Assistance, only participants with a SNAP Outreach enrollment or case are shown.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Case Manager</Label>
              <AppointmentCaseManagerSelect
                value={newAppointment.case_manager_id}
                staffList={staffList}
                onChange={(v) => setNewAppointment(prev => ({ ...prev, case_manager_id: v }))}
                testId="appointment-case-manager-select"
              />
              <p className="text-xs text-muted-foreground">Gets this appointment's alerts and reminders. Defaults to the participant's case manager.</p>
            </div>

            <div className="space-y-2">
              <Label>Location / Meeting Link</Label>
              <Input
                placeholder={newAppointment.location_type === 'video' ? 'Teams/Zoom link' : 'Address or room'}
                value={newAppointment.location}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, location: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes..."
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>

            {m365Connected && (
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-blue-600" />
                  <span className="text-sm">Sync to Microsoft 365 Calendar</span>
                </div>
                <input
                  type="checkbox"
                  checked={newAppointment.sync_to_m365}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, sync_to_m365: e.target.checked }))}
                  className="h-4 w-4"
                />
              </div>
            )}

            {/* Recurring Appointment Options - For types with SNAP E&T reminder trigger enabled */}
            {appointmentTypes.find(t => t.id === newAppointment.appointment_type)?.triggers_snap_et_reminder && (
              <div className="space-y-4 p-4 border border-purple-200 rounded-lg bg-purple-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Repeat className="w-4 h-4 text-purple-600" />
                    <Label className="font-medium">Make this a recurring appointment</Label>
                  </div>
                  <Switch
                    checked={newAppointment.is_recurring}
                    onCheckedChange={(checked) => setNewAppointment(prev => ({ 
                      ...prev, 
                      is_recurring: checked,
                      recurrence_end_date: checked ? format(addMonths(new Date(), 3), 'yyyy-MM-dd') : ''
                    }))}
                    data-testid="recurring-toggle"
                  />
                </div>

                {newAppointment.is_recurring && (
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Repeat Every</Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={newAppointment.recurrence_interval}
                            onChange={(e) => setNewAppointment(prev => ({ 
                              ...prev, 
                              recurrence_interval: parseInt(e.target.value) || 1 
                            }))}
                            className="w-20"
                          />
                          <Select
                            value={newAppointment.recurrence_unit}
                            onValueChange={(v) => setNewAppointment(prev => ({ ...prev, recurrence_unit: v }))}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="days">Days</SelectItem>
                              <SelectItem value="weeks">Weeks</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={newAppointment.recurrence_end_date}
                          min={newAppointment.date}
                          onChange={(e) => setNewAppointment(prev => ({ ...prev, recurrence_end_date: e.target.value }))}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Each occurrence will receive a separate 24-hour SMS reminder (if SNAP E&T).
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAppointment} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {newAppointment.is_recurring ? 'Create Recurring Appointments' : 'Create Appointment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Appointment</DialogTitle>
            <DialogDescription>
              Modify appointment details. Changes will sync to Outlook if connected.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
          {editingAppointment && (
            <>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingAppointment.title || ''}
                  onChange={(e) => setEditingAppointment(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>

              {/* Participant Field - Only show for SNAP Application Assistance appointments */}
              {isSnapApplicationAssistance(editingAppointment.appointment_type) && (
                <div className="space-y-2">
                  <Label>Participant (Optional)</Label>
                  <Select
                    value={editingAppointment.participant_id || '_none'}
                    onValueChange={(value) => {
                      const participant = snapOutreachParticipants.find(p => p.id === value);
                      setEditingAppointment(prev => ({
                        ...prev,
                        participant_id: value === '_none' ? null : value,
                        participant_name: value === '_none' ? '' : (participant?.full_name || '')
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingSnapParticipants ? "Loading..." : "Select participant"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No participant</SelectItem>
                      {snapOutreachParticipants.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Only participants with SNAP Outreach enrollment are shown. Linking a participant will make this appointment visible on their case.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={editingAppointment.date || ''}
                    onChange={(e) => setEditingAppointment(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={editingAppointment.appointment_type || ''}
                    onValueChange={(value) => setEditingAppointment(prev => ({ ...prev, appointment_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {appointmentTypes.map(type => (
                        <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={editingAppointment.start_time || ''}
                    onChange={(e) => setEditingAppointment(prev => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={editingAppointment.end_time || ''}
                    onChange={(e) => setEditingAppointment(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={editingAppointment.location || ''}
                  onChange={(e) => setEditingAppointment(prev => ({ ...prev, location: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Status *</Label>
                <Select
                  value={editingAppointment.status || 'Scheduled'}
                  onValueChange={(value) => {
                    setEditingAppointment(prev => ({ ...prev, status: value }));
                    // Show reschedule form if Rescheduled is selected
                    if (value === 'Rescheduled' && !editingAppointment.rescheduled_to_date) {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      setEditingAppointment(prev => ({
                        ...prev,
                        status: value,
                        rescheduled_to_date: format(tomorrow, 'yyyy-MM-dd'),
                        rescheduled_to_time: prev.start_time || '09:00'
                      }));
                    }
                  }}
                  data-testid="edit-appointment-status"
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Scheduled">Scheduled</SelectItem>
                    <SelectItem value="Attended">Attended</SelectItem>
                    <SelectItem value="Rescheduled">Rescheduled</SelectItem>
                    <SelectItem value="No Show">No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reschedule form when Rescheduled status is selected */}
              {editingAppointment.status === 'Rescheduled' && (
                <div className="space-y-3 p-4 border rounded-lg bg-blue-50 border-blue-200">
                  <Label className="font-medium text-blue-800">Reschedule To:</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-sm">New Date *</Label>
                      <Input
                        type="date"
                        value={editingAppointment.rescheduled_to_date || ''}
                        onChange={(e) => setEditingAppointment(prev => ({ ...prev, rescheduled_to_date: e.target.value }))}
                        min={format(new Date(), 'yyyy-MM-dd')}
                        data-testid="reschedule-date-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">New Time *</Label>
                      <Input
                        type="time"
                        value={editingAppointment.rescheduled_to_time || ''}
                        onChange={(e) => setEditingAppointment(prev => ({ ...prev, rescheduled_to_time: e.target.value }))}
                        data-testid="reschedule-time-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-blue-700">A new appointment will be created with the new date/time.</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editingAppointment.notes || ''}
                  onChange={(e) => setEditingAppointment(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Outlook sync indicator - hidden for SNAP Application Assistance appointments */}
              {editingAppointment.m365_event_id && !isSnapApplicationAssistance(editingAppointment.appointment_type) && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <Check className="w-4 h-4 text-blue-600" />
                  <span className="text-sm">This appointment is synced to Outlook</span>
                </div>
              )}
              
              {/* Recurring appointment info */}
              {editingAppointment.is_recurring && editingAppointment.series_id && (
                <div className="flex items-center gap-2 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                  <Repeat className="w-4 h-4 text-purple-600" />
                  <span className="text-sm">
                    Recurring appointment (Occurrence #{editingAppointment.occurrence_number})
                  </span>
                </div>
              )}
              
              {/* Initiate Communication Shortcut */}
              {editingAppointment.participant_id && editingAppointment.appointment_type === 'follow_up' && (
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2"
                  onClick={() => {
                    setShowEditDialog(false);
                    navigate(`/participants/${editingAppointment.participant_id}?tab=communications`);
                  }}
                  data-testid="initiate-communication-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  Initiate Communication
                </Button>
              )}
            </>
          )}
          </DialogBody>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {editingAppointment?.is_recurring && editingAppointment?.series_id && (
              <Button
                variant="destructive"
                size="sm"
                onClick={async () => {
                  if (window.confirm('Cancel all future appointments in this series?')) {
                    try {
                      await axios.delete(`${API}/appointments/series/${editingAppointment.series_id}?cancel_future_only=true`);
                      toast.success('Future appointments cancelled');
                      setShowEditDialog(false);
                      fetchAppointments();
                    } catch (error) {
                      toast.error('Failed to cancel series');
                    }
                  }
                }}
              >
                Cancel Series
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateAppointment} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentsPage;
